/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { type ErrorComponentProps, useRouter } from '@tanstack/react-router';

import { Failed } from '@/components/ui/States';
import { isNewVersionError, reloadForNewVersion } from '@/lib/newVersion';

/**
 * What a screen shows when it could not be opened.
 *
 * A screen whose file is gone because Vellum was updated since the page was
 * loaded is loaded again, once, rather than shown as a failure (see
 * `lib/newVersion`). Anything else is the ordinary error state, which never
 * shows raw error text (`UX-3`).
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  if (isNewVersionError(error) && reloadForNewVersion()) {
    return <div className="wrap" />;
  }
  const updated = isNewVersionError(error);
  return (
    <div className="wrap">
      <Failed
        title={updated ? 'Vellum has been updated' : 'This screen could not be opened'}
        detail={
          updated
            ? 'Reload the page to get the new version.'
            : 'Try again. If it keeps happening, reload the page.'
        }
        onRetry={
          updated
            ? () => {
                window.location.reload();
              }
            : () => {
                // Resetting the boundary alone shows the same error again: a
                // route that failed before it loaded -- the session check,
                // when the API could not be reached -- has to be run again,
                // which is what invalidating does, resetting the boundary
                // with it.
                reset();
                void router.invalidate();
              }
        }
      />
    </div>
  );
}
